// crewbit-admin/src/lib/invoiceSettingsMutations.ts
//
// §Money.StubActionSweepCompletion Phase 4 + §Money.HardeningRetirementRefund
// Phase 4 — canonical write path for invoice_settings columns (Decisions
// #580 + #584). Calls the `update_invoice_settings` SECDEF RPC (Migrations
// 292 + 296) with the COALESCE-extends pattern: only fields explicitly
// passed get touched.
//
// Mirrors crewbit-mobile/lib/invoiceSettingsMutations.ts.

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';
import { MONEY_MUTATION_EVENT } from '../hooks/useFinancialSummary';

export type LateFeeType = 'none' | 'percent' | 'flat';

/** Method strings stored inside `invoice_settings.accepted_methods` jsonb array. */
export type AcceptedMethod = 'card' | 'etransfer' | 'cash';

export interface UpdateInvoiceSettingsArgs {
  businessId: string;
  acceptedMethods?: AcceptedMethod[] | null;
  lateFeeType?: LateFeeType | null;
  lateFeeAmount?: number | null;
  acceptCreditCard?: boolean | null;
  // §Settings Phase 2B (Migration 20260519021315): acceptEtransfer removed.
  // Derive e-Transfer affordance from acceptedMethods.includes('etransfer').
  etransferEmail?: string | null;
  stripePaymentsEnabled?: boolean | null;
  /** Optional client-side dedup key. Server is permissive (no idempotency_key column). */
  idempotencyKey?: string | null;
  // Brand-only args added Migration 296 / Decision #584:
  logoUrl?: string | null;
  accentColor?: string | null;
  defaultNote?: string | null;
  defaultThankYou?: string | null;
  invoicePrefix?: string | null;
}

export type UpdateInvoiceSettingsResult = MoneyRpcResult<{ business_id: string }>;

export async function updateInvoiceSettings(
  args: UpdateInvoiceSettingsArgs,
): Promise<UpdateInvoiceSettingsResult> {
  const result = await callMoneyRpc<{ business_id: string }>('update_invoice_settings', {
    p_business_id: args.businessId,
    p_accepted_methods: args.acceptedMethods ?? null,
    p_late_fee_type: args.lateFeeType ?? null,
    p_late_fee_amount: args.lateFeeAmount ?? null,
    p_accept_credit_card: args.acceptCreditCard ?? null,
    p_etransfer_email: args.etransferEmail ?? null,
    p_stripe_payments_enabled: args.stripePaymentsEnabled ?? null,
    p_idempotency_key: args.idempotencyKey ?? null,
    p_logo_url: args.logoUrl ?? null,
    p_accent_color: args.accentColor ?? null,
    p_default_note: args.defaultNote ?? null,
    p_default_thank_you: args.defaultThankYou ?? null,
    p_invoice_prefix: args.invoicePrefix ?? null,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}
